import { useQuery } from '@tanstack/react-query'
import { Loader2, UserPlus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import apiClient from '@/api/client'
import { cn, formatCurrency } from '@/lib/utils'
import type { HouseAccount } from '@/types'

/**
 * F-MM-N31 — staff who eat on an allowance but have no POS login (cleaners,
 * helpers), plus allowances made on a customer before allowances moved here.
 */
export function NoLoginAllowancesCard({
  canAdd,
  onAdd,
  onOpen,
}: {
  canAdd: boolean
  onAdd: () => void
  onOpen: (account: HouseAccount) => void
}) {
  const accounts = useQuery({
    queryKey: ['house-accounts', 'staff-allowances'],
    queryFn: () => apiClient.listHouseAccounts({ kind: 'staff_allowance' }),
  })
  const people = (accounts.data?.data ?? []).filter((a) => !a.has_login && a.status !== 'closed')

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <CardTitle className="text-base">People without a POS login</CardTitle>
          <CardDescription>Staff who eat on a meal allowance but don’t use the POS, like a cleaner or helper.</CardDescription>
        </div>
        {canAdd && (
          <Button variant="outline" size="sm" onClick={onAdd} className="gap-2 self-start">
            <UserPlus className="h-4 w-4" aria-hidden />
            Add person
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {accounts.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
          </div>
        ) : people.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nobody yet.{canAdd ? ' Add someone who eats on an allowance but doesn’t sign in to the POS.' : ''}
          </p>
        ) : (
          <ul className="divide-y rounded-md border">
            {people.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => onOpen(a)}
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-muted/50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{a.person_name}</span>
                      {a.status === 'suspended' && <Badge variant="secondary">Paused</Badge>}
                    </div>
                    {a.holder_phone && <div className="truncate text-xs text-muted-foreground">{a.holder_phone}</div>}
                  </div>
                  <AllowanceUsage used={a.allowance_used + a.overage_this_month} allowance={a.monthly_allowance} over={a.overage_this_month} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

/** "Rs 2,400 of Rs 6,000" with the overage under it. Shared with the staff table. */
export function AllowanceUsage({
  used,
  allowance,
  over,
  align = 'right',
}: {
  used: number
  allowance: number
  over: number
  align?: 'left' | 'right'
}) {
  return (
    <div className={cn('text-sm tabular-nums', align === 'right' ? 'text-right' : 'text-left')}>
      <div className={cn(over > 0 && 'text-warning-fg')}>
        {formatCurrency(used)} <span className="text-muted-foreground">of {formatCurrency(allowance)}</span>
      </div>
      <div className={cn('text-xs', over > 0 ? 'text-warning-fg' : 'text-muted-foreground')}>
        {over > 0 ? `${formatCurrency(over)} over` : 'this month'}
      </div>
    </div>
  )
}
